// src/pages/StaffDashboard.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';

const StaffDashboard: React.FC = () => {
  const { user, role, loading } = useUserRole();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalClients: 0,
    activeTasks: 0,
    pendingTasks: 0
  });

  useEffect(() => {
    if (!loading && role !== 'staff' && role !== 'admin') {
      navigate('/client-dashboard');
    }
  }, [loading, role, navigate]);

  useEffect(() => {
    if (role === 'staff' || role === 'admin') {
      loadStats();
    }
  }, [role]);

  const loadStats = async () => {
    try {
      // Get clients
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      
      // Get tasks (if collection exists)
      let activeTasks = 0;
      let pendingTasks = 0;
      try {
        const tasksSnapshot = await getDocs(collection(db, 'tasks'));
        tasksSnapshot.forEach((doc) => {
          const task = doc.data();
          if (task.status === 'pending' || task.status === 'in-progress') {
            pendingTasks++;
          }
          if (task.status === 'in-progress' || task.status === 'completed') {
            activeTasks++;
          }
        });
      } catch (error) {
        // Tasks collection may not exist yet
      }

      setStats({
        totalClients: clientsSnapshot.size,
        activeTasks,
        pendingTasks
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (role !== 'staff' && role !== 'admin') {
    return null;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <h1>Staff Dashboard</h1>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Welcome, {user?.email}. Manage client tasks and services from here.
        </p>

        {/* Statistics Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <StatCard 
            title="Total Clients" 
            value={stats.totalClients} 
            color="#28a745"
            icon="🏢"
          />
          <StatCard 
            title="Active Tasks" 
            value={stats.activeTasks} 
            color="#007bff"
            icon="📋"
          />
          <StatCard 
            title="Pending Tasks" 
            value={stats.pendingTasks} 
            color="#ffc107"
            icon="⏳"
          />
        </div>

        {/* Quick Actions */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginTop: 0 }}>Quick Actions</h2>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <ActionButton 
              onClick={() => navigate('/clients')}
              label="View All Clients"
              icon="🏢"
            />
            <ActionButton 
              onClick={() => navigate('/tasks')}
              label="Manage Tasks"
              icon="📋"
            />
            <ActionButton 
              onClick={() => navigate('/tasks?filter=create')}
              label="Create New Task"
              icon="➕"
            />
          </div>
        </div>

        {/* Recent Activity Placeholder */}
        <div style={{ 
          backgroundColor: '#e9ecef', 
          padding: '1.5rem', 
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>Recent Activity</h3>
          <p style={{ color: '#666', fontStyle: 'italic' }}>
            Activity feed will appear here once tasks and client interactions are tracked.
          </p>
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  color: string;
  icon: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, color, icon }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
          {title}
        </div>
        <div style={{ fontSize: '2rem', fontWeight: 'bold', color }}>
          {value}
        </div>
      </div>
      <div style={{ fontSize: '2.5rem' }}>
        {icon}
      </div>
    </div>
  </div>
);

interface ActionButtonProps {
  onClick: () => void;
  label: string;
  icon: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({ onClick, label, icon }) => (
  <button
    onClick={onClick}
    style={{
      padding: '0.75rem 1.5rem',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '1rem'
    }}
  >
    <span>{icon}</span>
    <span>{label}</span>
  </button>
);

export default StaffDashboard;



